
import { Component, Vue } from "vue-property-decorator";
import CanvasCourseMappingApiService from "@/services/CanvasCourseMappingApiService";
import TrainingApiService from "@/services/TrainingApiService";
import EnumerationApiService from "@/services/EnumerationApiService";
import CanvasCourseMapping from "@/types/Canvas/CanvasCourseMapping";
import EnumerationList from "@/types/Enumeration/EnumerationList";
import TrainingType from "@/types/Training/TrainingType";
import CanvasCourseMappingForm from "@/components/canvasMapping/CanvasCourseMappingForm.vue";
import CanvasSyncConfigPanel from "@/components/canvasMapping/CanvasSyncConfigPanel.vue";
import ConfirmButton from "@/components/ConfirmButton.vue";

@Component({
  components: {
    CanvasCourseMappingForm,
    CanvasSyncConfigPanel,
    ConfirmButton
  }
})
export default class CanvasCourseMappingList extends Vue {
  loading: boolean = false;

  api = new CanvasCourseMappingApiService();
  trainingApi = new TrainingApiService();
  enumApi = new EnumerationApiService();

  mappings: CanvasCourseMapping[] = [];
  trainingTypes: TrainingType[] = [];
  trainingDeliveredList: EnumerationList = new EnumerationList();
  skillsList: EnumerationList = new EnumerationList();
  deliveryFormatsList: EnumerationList = new EnumerationList();

  currentMapping: CanvasCourseMapping = new CanvasCourseMapping();
  formDialog: boolean = false;
  dialogTitle: string = "";

  headers: any[] = [
    { text: this.$root.$t("CanvasCourseId"), value: "CanvasCourseId" },
    { text: this.$root.$t("TrainingName"), value: "TrainingName" },
    { text: this.$root.$t("Hours"), value: "Hours" },
    { text: this.$root.$t("IsActive"), value: "IsActive", sortable: false },
    { text: "", value: "actions", sortable: false }
  ];

  // Rows for the data-table: spread the mapping so column bindings still work,
  // plus a derived `TrainingName` that shows OtherTrainingName when set (the "Other"
  // catalog item carries no useful label) and otherwise the resolved enumeration
  // label. Sorting/filtering see the
  // resolved string, so users can sort by either source uniformly.
  get rowsWithDisplay(): any[] {
    const enumItems =
      (this.trainingDeliveredList &&
        this.trainingDeliveredList.EnumerationItems) ||
      [];
    const byId = new Map<string, string>();
    for (const eli of enumItems) {
      const e = eli.Enumeration;
      const label = e.LocalizationKey
        ? this.$t(e.LocalizationKey).toString()
        : e.Description || e.Value || "";
      byId.set(eli.EnumerationId, label);
    }

    return this.mappings.map(m => {
      const fromEnum = m.TrainingDeliveredId
        ? byId.get(m.TrainingDeliveredId)
        : "";
      // "Other" mappings carry the real name in OtherTrainingName.
      const display = m.OtherTrainingName || fromEnum || "";
      return { ...m, TrainingName: display };
    });
  }

  mounted() {
    this.fetchData();
  }

  async fetchData() {
    this.loading = true;
    try {
      const [mappingsResp, typesResp, enumsResp] = await Promise.all([
        this.api.GetMappings(),
        this.trainingApi.GetTrainingTypes(),
        this.enumApi.GetEnumerationListsBySystemName([
          "TrainingDelivered",
          "SkillsAndCompetencies",
          "TrainingDeliveryFormats"
        ])
      ]);

      if (mappingsResp.Success) this.mappings = mappingsResp.Value;
      if (typesResp.Success) this.trainingTypes = typesResp.Value;
      if (enumsResp.Success) {
        const enums: EnumerationList[] = enumsResp.Value;
        this.trainingDeliveredList =
          enums.find(e => e.SystemName === "TrainingDelivered") ||
          new EnumerationList();
        this.skillsList =
          enums.find(e => e.SystemName === "SkillsAndCompetencies") ||
          new EnumerationList();
        this.deliveryFormatsList =
          enums.find(e => e.SystemName === "TrainingDeliveryFormats") ||
          new EnumerationList();
      }
    } finally {
      this.loading = false;
    }
  }

  addNew() {
    this.dialogTitle = this.$t("AddNewCanvasCourseMapping").toString();
    const fresh = new CanvasCourseMapping();
    // Default to NationalWorkshopTraining if present (matches the auto-log path).
    const def = this.trainingTypes.find(
      t => t.Name === "NationalWorkshopTraining"
    );
    if (def) fresh.TrainingTypeId = def.Id;
    this.currentMapping = fresh;
    this.formDialog = true;
  }

  edit(item: CanvasCourseMapping) {
    this.dialogTitle = this.$t("EditCanvasCourseMapping").toString();
    this.currentMapping = { ...item };
    this.formDialog = true;
  }

  onDelete(item: CanvasCourseMapping) {
    this.api.DeleteMapping(item.Id).then(resp => {
      if (resp.Success) {
        this.mappings.splice(this.mappings.indexOf(item), 1);
      }
    });
  }

  onSaved() {
    this.formDialog = false;
    this.fetchData();
  }
}
